
import React, { useState } from 'react';
import { DetailedBotAccount } from '@/types/trader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Power, ChevronDown, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AccountDetails } from '@/components/accounts/trader-card/AccountDetails';
import { AccountTypeBadge } from '@/components/common/AccountTypeBadge';
import { ReversePositionBadge } from '@/components/common/ReversePositionBadge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Wallet } from 'lucide-react';
import { formatNumber } from '@/components/premium-bots/utils/formatting';
import { useTranslation } from 'react-i18next';

interface CTraderAccountCardProps {
  account: DetailedBotAccount;
  onToggleStatus: (accountId: string, isActive: boolean) => Promise<void>;
  onDelete: (accountId: string) => Promise<void>;
  isProcessing?: boolean;
}

export function CTraderAccountCard({
  account,
  onToggleStatus,
  onDelete,
  isProcessing = false,
}: CTraderAccountCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isConnected = account.status === 'connected';
  const isLive = account.trading_account?.live || false;
  const tradingAccountNumber = account.trading_account?.number_account_trading || "Unknown";
  const accountBalance = account.trading_account?.balance_account || 0;
  const brokerOrPropFirm = account.csp_account?.broker || account.csp_account?.prop_firm;
  const { t } = useTranslation();

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="w-full transition-all duration-200"
    >
      <Card className="overflow-hidden border hover:border-primary/20 transition-all duration-200">
        <div className="p-4">
          <CollapsibleTrigger asChild className="w-full">
            <div className="flex justify-between items-start cursor-pointer">
              <div className="flex flex-col">
                <span className="font-semibold text-base font-mono">
                  {tradingAccountNumber}
                </span>
                <span className="text-sm text-muted-foreground truncate max-w-[180px]">
                  {account.csp_account?.csp_account_name || 'Unknown'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <AccountTypeBadge isLive={isLive} />
                <ReversePositionBadge isReverse={account.is_reverse_position} size="sm" />
                <ChevronDown 
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
                />
              </div>
            </div>
          </CollapsibleTrigger>

          <div className="flex justify-between items-center mt-3">
            <div className="flex items-center">
              <Wallet className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
              <span className="text-sm font-medium">
                ${formatNumber(accountBalance)}
              </span>
            </div>
            <Badge 
              variant={isConnected ? "default" : "outline"} 
              className={`text-xs h-5 ${
                isConnected 
                  ? 'bg-green-500/20 text-green-700 hover:bg-green-500/30 border-green-500/30' 
                  : ''
              }`}
            >
              {isConnected ? t('connected') : t('disconnected')}
            </Badge>
          </div>

          <CollapsibleContent>
            <div className="mt-4 pt-4 border-t">
              <AccountDetails
                brokerOrPropFirm={brokerOrPropFirm}
                volumeMultiplier={account.volume_multiplier?.toString() || '1.0'}
                apiStatus={account.api_connection?.status || 'Unknown'}
                addedDate={account.added_date}
                createdAt={account.created_at || ''}
              />

              <div className="flex justify-between items-center mt-4 pt-3 border-t">
                <div className="flex items-center gap-2">
                  <Power size={14} />
                  <span className="text-sm">{t('active')}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Switch 
                    checked={isConnected}
                    onCheckedChange={(checked) => {
                      onToggleStatus(account.id, checked);
                    }}
                    disabled={isProcessing}
                  />

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-destructive hover:bg-destructive/10"
                        disabled={isProcessing}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('delete_trading_account')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('delete_account_confirmation')}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => onDelete(account.id)} 
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {t('delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Card>
    </Collapsible>
  );
}
